<template>
  <ExpansionNav ref="collapse" />
  <el-scrollbar class="scrollbar-container" @scroll="onScroll">
    <el-menu
      :collapse="isCollapse"
      :collapse-transition="false"
      :default-active="activeIndex"
      :default-openeds="['1']"
      class="el-menu-vertical"
    >
      <template v-for="item in menus" :key="item.path">
        <template v-if="onPageStatus(item)">
          <template v-if="isCollapse">
            <el-tooltip effect="dark" :content="item.name" placement="right">
              <el-menu-item
                v-permission="item.permission"
                :index="item.path"
                @click="pageHopping(item)"
              >
                <NavIcon :data="item" />
              </el-menu-item>
            </el-tooltip>
          </template>
          <template v-else>
            <el-menu-item
              v-permission="item.permission"
              :index="item.path"
              @click="pageHopping(item)"
            >
              <NavIcon :data="item" />
              <span class="nav-parent-title">{{ item.name }}</span>
            </el-menu-item>
          </template>
        </template>
      </template>
    </el-menu>
    <div class="deal-text-link">
      <p>
        <span
          class="hover:cursor-pointer hover:underline"
          @click="openUserAgreementPage(AGREEMENT.SHIELD_PLATFORM_USER_PRIVACY_POLICY)"
        >
          《{{ t('common.userUseAgreement') }}》
        </span>
      </p>
      <p>
        <span
          class="hover:cursor-pointer hover:underline"
          @click="openUserAgreementPage(AGREEMENT.SHIELD_PLATFORM_USE_PROTOCOL)"
        >
          《{{ t('common.platformUsageProtocol') }}》
        </span>
      </p>
      <p>
        <span
          class="hover:cursor-pointer hover:underline"
          @click="openUserAgreementPage(AGREEMENT.SHIELD_PLATFORM_DEALER_OPERATION_MANUAL)"
        >
          《{{ t('common.dealerHandbook') }}》
        </span>
      </p>
      <p class="text-gray-400">Version: v{{ version }}</p>
    </div>
  </el-scrollbar>
</template>
<script lang="ts" setup>
import * as R from 'ramda'
import { useI18n } from 'vue-i18n'
import { ElLoading } from 'element-plus'
import { paths } from '@/router/paths'

import NavIcon from '@/layout/default/components/NavIcon.vue'
import ExpansionNav from '@/components/ExpansionNav.vue'
import { routerTo } from '@/utils/router'
import { useCommonStore } from '@/store/useCommonStore'
import { LOCALSTORAGE_KEYS, AGREEMENT } from '@/static/enums'
import pack from '../../../../package.json'
import APIS from '@/api/common'
import type { Menu } from '../../types/common.d'

const { t } = useI18n()
const route = useRoute()
const store = useCommonStore()

const collapse = ref()
const { version } = pack

let stopScrolling: () => void
const onScroll = ({ scrollTop }: { scrollTop: number }) => {
  if (scrollTop !== 0) stopScrolling = collapse.value.scrolling()
  else stopScrolling?.()
}

const roleId = localStorage.getItem(LOCALSTORAGE_KEYS.ROLE)
const isCollapse = computed(() => store.isCollapse)
/**
 * permission?: string 权限，具体需要访问的值需要从 store/accesses.ts 中获取
 * v-permission 为自定义指令
 */
const menus = computed<Menu[]>(() => [
  {
    name: t('menu.disk'),
    path: paths.disk,
    permission: 'MY_DISC',
    customIconName: 'net-disk',
  },
  {
    name: t('menu.department'),
    path: paths.department,
    permission: 'DEPARTMENT',
    customIconName: 'department',
  },
  {
    name: t('menu.role'),
    path: paths.role,
    permission: 'ROLE',
    customIconName: 'role',
  },
  {
    name: t('menu.user'),
    path: paths.user,
    permission: 'USER',
    customIconName: 'user',
  },
  {
    name: t('menu.folder'),
    path: paths.folder,
    permission: 'FOLDER_PERMISSION',
    customIconName: 'folder',
  },
  {
    name: t('menu.log'),
    path: paths.log,
    permission: 'LOG',
    customIconName: 'log',
  },
  {
    name: t('menu.dashboard'),
    path: paths.dashboard,
    permission: 'DASHBOARD',
    customIconName: 'dashboard',
  },
])

// 跳转页面
const pageHopping = ({ path, type }: Menu) => {
  if (!path) return
  routerTo({ path }, { type })
}

// 二级菜单高亮显示
const activeIndex = computed(() =>
  R.pipe(
    R.filter<Menu>((item) => R.startsWith((item.activePath ?? item.path) as string, route.path)),
    R.sort((a: Menu, b: Menu) => a.path.localeCompare(b.path)),
    R.last,
    R.defaultTo({} as any),
    R.prop('path'),
    R.defaultTo(undefined)
  )(menus.value)
)

// 权限
const onPageStatus = (item: any) => {
  const useRoles = store.userInfo?.roleMenuPermissionDTOS[roleId as string] || []
  return useRoles.some((yItem: any) => yItem.code === item.permission)
}

// 查看协议
// const openUserAgreementPage = (code: any) => {
//   const link = router.resolve({
//     path: `${paths.userAgreement}/${code}`,
//   })
//   window.open(link.href, '_blank')
// }
async function openUserAgreementPage(code: any) {
  const loading = ElLoading.service({
    lock: true,
    text: 'Loading',
    background: 'rgba(255, 255, 255, 0.9)',
  })
  try {
    const res = await APIS.getUserTemplate({ code })
    console.log('ccc', res)
    window.open(res.data?.path)
    loading.close()
  } catch (err) {
    loading.close()
  }
}
</script>

<style lang="scss" scoped>
.el-menu {
  border-right: 0;
}
.el-menu-vertical {
  @apply bg-[rgba(0,0,0,0.3)];

  &:not(.el-menu--collapse) {
    width: 225px;
    height: calc(100vh - 120px);
  }

  :deep(.el-menu-item),
  :deep(.el-sub-menu__title) {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 48px;
    padding: 0 0 0 30px !important;
    @apply text-gray-400;

    .icon-box,
    .nav-parent-title {
      color: inherit !important;
    }

    .nav-parent-title {
      @apply mt-[2px] text-[16px] leading-[15px];
    }

    &:not(.is-active) {
      background: none;
    }
    &:not(.is-active):hover {
      @apply text-white;
    }
    &.is-active {
      background: #00adef;

      .icon-box,
      .nav-parent-title {
        @apply text-white #{!important};
      }
    }
    &.is-button {
      background: transparent !important;
      @apply cursor-default;

      .icon-box {
        visibility: hidden;
      }
      .nav-parent-title {
        margin-left: -12px;
        @apply px-3;
        @apply font-normal;
        line-height: 32px;
        @apply rounded-sm;
        @apply cursor-pointer;

        &:hover {
          @apply text-[#00ADEF];
          background: rgba(0, 0, 0, 0.1);
        }
      }
    }
  }

  .user-setting-status {
    width: 16px;
    height: 16px;
    margin: 4px 0 0 auto;

    &.user-setting-status--collapse {
      @apply absolute right-[12px];
    }
  }

  &.horizontal-collapse-transition {
    .nav-parent-title {
      opacity: 0;
    }
  }
  &.el-menu--collapse {
    width: 84px;

    :deep(.collapse-box),
    :deep(.el-menu-item) {
      width: 100%;
      //padding: 0 16px !important;

      .nav-parent-title {
        opacity: 0;
      }

      &.is-button {
        .icon-box {
          margin-left: -1px;
          visibility: visible;
          @apply cursor-pointer;

          .el-icon {
            @apply text-white;
            font-size: 20px;
          }

          &:hover {
            @apply text-[#00ADEF];
            background: rgba(0, 0, 0, 0.1);
          }
        }
      }
    }
    :deep(.icon-box) {
      margin-right: 0;
    }
  }
}

.scrollbar-container {
  height: calc(100% - 44px);

  :deep(.el-scrollbar__wrap) {
    max-height: calc(100vh - 104px);
  }
}

.deal-text-link {
  width: 100%;
  position: absolute;
  bottom: 10px;
  p {
    color: white;
    text-align: center;
    font-size: 13px;
    margin-bottom: 8px;
    span {
      span {
        cursor: pointer;
        border-bottom: 1px solid white;
      }
      &:hover {
        color: rgb(64, 158, 255);
        span {
          border-color: rgb(64, 158, 255);
        }
      }
    }
  }
}
</style>
